import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query'

import InactivityHandler from '@shared/components/src/InactivityHandler'
import { TrackUserActivity } from '@shared/components/src/KeepSessionAlive'
import useCurrentUserId from '@shared/hooks/src/useCurrentUserId'
import { postConsentRedirectKey } from '@shared/hooks/src/useQueryDeepLink'
import DropdownOptionsProvider from '@shared/providers/src/DropdownOptionsProvider'
import FeatureFlagsProvider from '@shared/providers/src/FeatureFlagsProvider'
import { useMe } from '@shared/providers/src/MeProvider'
import SettingsProvider from '@shared/providers/src/SettingsProvider'
import API from '@shared/services/src/API'
import { PatientCBOStatus, QK, UserRole } from '@shared/utils'

import MeSuspenseProvider from '@providers/MeProvider'
import ConsentDialog from '@pages/Consent'
import { NeedsBookingPrompt } from '@components/LeavePrompt'
import Notifications from '@components/Notifications'
import { inactivityTime } from '@config'

import { useConsentsDone } from './AuthenticatedProviders.hooks'
import { SubscribeDialog, WaitlistDialog } from './AuthenticatedProviders.utils'

export default function AuthenticatedProviders() {
  const userId = useCurrentUserId()
  if (!userId) return null
  return <HeartbeatGuard />
}

function HeartbeatGuard() {
  useSuspenseQuery({
    queryKey: QK.heartbeat.check,
    queryFn: () => API.heartbeat.check(null, undefined),
    staleTime: 0,
  })

  return <DataGuard />
}

function DataGuard() {
  const userId = useCurrentUserId()

  useSuspenseQueries({
    queries: [
      {
        queryKey: QK.me(userId).details,
        queryFn: () => API.me.details(),
      },
      {
        queryKey: QK.platform.constants.list,
        queryFn: () => API.platform.constants.list(),
      },
      {
        queryKey: QK.platform.settings.list,
        queryFn: () => API.platform.settings.list(),
      },
      {
        queryKey: QK.platform.features.list,
        queryFn: () => API.platform.features.list(),
      },
    ],
  })

  return (
    <DropdownOptionsProvider>
      <SettingsProvider>
        <FeatureFlagsProvider>
          <MeSuspenseProvider>
            <SystemUserGuard />
          </MeSuspenseProvider>
        </FeatureFlagsProvider>
      </SettingsProvider>
    </DropdownOptionsProvider>
  )
}

function SystemUserGuard() {
  const me = useMe()
  const isSystemUser = me.role !== UserRole.Patient

  useEffect(() => {
    if (isSystemUser) {
      window.location = `${import.meta.env.VITE_ALLY_DOMAIN}/app`
    }
  }, [isSystemUser])

  if (isSystemUser) return null
  return <PatientDataGuard />
}

function PatientDataGuard() {
  const userId = useCurrentUserId()

  useSuspenseQueries({
    queries: [
      {
        queryKey: QK.patients.id(userId).photoID,
        queryFn: () => API.patients.id(userId).photoID.details(),
      },
      {
        queryKey: QK.patients.id(userId).consents.list({ all: false }),
        queryFn: () => API.patients.id(userId).consents.list({ all: false }),
      },
      {
        queryKey: QK.patients.id(userId).schedulingEligibilities.list(),
        queryFn: () => API.patients.id(userId).schedulingEligibilities.list(),
      },
    ],
  })

  return <ConsentGuard />
}

function ConsentGuard() {
  const consentsDone = useConsentsDone()

  if (!consentsDone) return <ConsentDialog />

  const to = localStorage.getItem(postConsentRedirectKey)

  // Handle post-consent redirect
  if (to) {
    localStorage.removeItem(postConsentRedirectKey)
    return <Navigate to={`/redirect?to=${to}`} />
  }

  return <CBOGuard />
}

function CBOGuard() {
  const me = useMe()

  if (!me.cbo) return <Application />

  return <CBOVerificationGuard />
}

function CBOVerificationGuard() {
  const me = useMe()
  const { pathname } = useLocation()

  const { data: cboVerification } = useSuspenseQuery({
    queryKey: QK.patients.id(me.id).cbos.id(me.cbo.id).verify.list(),
    queryFn: () => API.patients.id(me.id).cbos.id(me.cbo.id).verify(),
  })

  const isMessaging = pathname.startsWith('/app/messages')

  if (cboVerification.action === PatientCBOStatus.NotAllowed && !me.subscribed && !isMessaging)
    return <SubscribeDialog reason={cboVerification.reason} />
  if (cboVerification.action === PatientCBOStatus.Waitlist && [null, undefined].includes(me.waitlist)) return <WaitlistDialog />

  return <Application />
}

function Application() {
  return (
    <>
      <Outlet />
      <NeedsBookingPrompt />
      <Notifications />
      <TrackUserActivity />
      <InactivityHandler interval={inactivityTime} />
    </>
  )
}
